import React from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox, Form, InputNumber, Select, TimePicker
} from 'antd';
import settings from 'settings';
import _ from 'lodash';
import { assignmentTitleFormats } from 'components/Timeline/ItemRenderer';
import moment from 'moment-timezone';
import GenericErrorBoundary from 'components/common/GenericErrorBoundary';

const { APP_CONFIG } = settings;
const FormItem = Form.Item;
const { Option } = Select;
const titleFormats = Object.keys(assignmentTitleFormats);

const mapPropsToFields = ({ dataSource }) => {
  const activityCreateCount = dataSource.activityCreateCount.toString();
  const activityHoursBetween = dataSource.activityHoursBetween.toString();
  const timelineMaxDays = dataSource.timelineMaxDays.toString();
  const timelineSnapMinutes = dataSource.timelineSnapMinutes.toString();
  const {
    displayTravelLayer, displayLeaveLayer, displayAvailabilityLayer, rosterPivoted, hideEmptyRows, assignmentTitleFormat, timeFilterStart, timeFilterEnd, hideUnallocatedRows
  } = dataSource;

  return {
    activityCreateCount: Form.createFormField({ value: activityCreateCount }),
    activityHoursBetween: Form.createFormField({ value: activityHoursBetween }),
    timelineMaxDays: Form.createFormField({ value: timelineMaxDays }),
    timelineSnapMinutes: Form.createFormField({ value: timelineSnapMinutes }),
    displayTravelLayer: Form.createFormField({ value: displayTravelLayer }),
    displayLeaveLayer: Form.createFormField({ value: displayLeaveLayer }),
    displayAvailabilityLayer: Form.createFormField({ value: displayAvailabilityLayer }),
    rosterPivoted: Form.createFormField({ value: rosterPivoted }),
    hideEmptyRows: Form.createFormField({ value: hideEmptyRows }),
    assignmentTitleFormat: Form.createFormField({ value: assignmentTitleFormat }), // provide default
    timeFilterStart: Form.createFormField({ value: timeFilterStart instanceof moment ? timeFilterStart : null }), // if not a valid format ignore
    timeFilterEnd: Form.createFormField({ value: timeFilterEnd instanceof moment ? timeFilterEnd : null }), // if not a valid format ignore,
    hideUnallocatedRows: Form.createFormField({ value: hideUnallocatedRows })
  };
};

const onFieldsChange = (props, fields) => {
  const { onFieldsChange: dispatchOnFieldsChange } = props;
  if (dispatchOnFieldsChange) {
    dispatchOnFieldsChange(fields);
  }
};

const onValuesChange = (props, values) => {
  const { onValuesChange: dispatchOnValuesChange } = props;
  if (dispatchOnValuesChange) {
    dispatchOnValuesChange(values);
  }
};

const FormInput = ({ readonly, form, optionFields }) => {
  const { getFieldDecorator } = form;
  const {
    showActivityCreateCount,
    showActivityHoursBetween,
    showTimelineMaxDays,
    showTimelineSnapMinutes,
    showDisplayTravelLayer,
    showDisplayLeaveLayer,
    showDisplayAvailabilityLayer,
    showRosterPivot,
    showHideEmptyRows,
    showAssignmentTitleFormat,
    showTimeFilter,
    showHideUnallocatedRows
  } = optionFields;

  const layout = {
    inputLayout: {
      labelCol: {
        xs: { span: 24 },
        sm: { span: 12 }
      },
      wrapperCol: {
        xs: { span: 24 },
        sm: { span: 12 }
      }
    },
    fullWidthInputLayout: {
      wrapperCol: {
        xs: { span: 24 },
        sm: { span: 24 }
      }
    }
  };
  const timeFormat = 'HH:mm';
  return (
    <Form>
      {(showActivityCreateCount || showActivityHoursBetween) && (
        <fieldset>
          <legend>Activity Assignment</legend>
          {showActivityCreateCount && (
            <FormItem {...layout.inputLayout} label="No. of activities">
              {getFieldDecorator('activityCreateCount')(
                <InputNumber disabled={readonly.activityCreateCount} min={1} max={100} />
              )}
            </FormItem>
          )}
          {showActivityHoursBetween && (
            <FormItem {...layout.inputLayout} label="Repeat Cycle Hours">
              {getFieldDecorator('activityHoursBetween')(
                <InputNumber disabled={readonly.activityHoursBetween} min={1} max={744} />
              )}
            </FormItem>
          )}
          <br />
          {showHideUnallocatedRows && <legend>Unallocated Assignments</legend>}
          {showHideUnallocatedRows && (
            <FormItem {...layout.inputLayout} label="Hide Unallocated Row">
              {getFieldDecorator('hideUnallocatedRows', { valuePropName: 'checked', initialValue: true })(
                <Checkbox disabled={readonly.hideUnallocatedRows} style={{ paddingLeft: '5px' }} />
              )}
            </FormItem>
          )}
        </fieldset>
      )}
      {(showTimelineMaxDays || showTimelineSnapMinutes || showDisplayTravelLayer || showDisplayLeaveLayer || showDisplayAvailabilityLayer || showRosterPivot || showHideEmptyRows || showAssignmentTitleFormat || showTimeFilter) && (
        <fieldset>
          <legend>Timeline Options</legend>
          {showTimelineMaxDays && (
            <FormItem {...layout.inputLayout} label="Days to show">
              {getFieldDecorator('timelineMaxDays')(
                <InputNumber disabled={readonly.timelineMaxDays} min={1} max={120} />
              )}
            </FormItem>
          )}
          {showTimelineSnapMinutes && (
            <FormItem {...layout.inputLayout} label="Snap minutes">
              {getFieldDecorator('timelineSnapMinutes')(
                <InputNumber disabled={readonly.timelineSnapMinutes} min={APP_CONFIG.minActivityDurationMinute} max={120} />
              )}
            </FormItem>
          )}
          {showRosterPivot && (
            <FormItem {...layout.inputLayout} label="Pivot Roster">
              {
                getFieldDecorator('rosterPivoted', { valuePropName: 'checked' })(
                  <Checkbox disabled={readonly.rosterPivot} style={{ paddingLeft: '5px' }} />
                )
              }
            </FormItem>
          )}
          {showHideEmptyRows && (
            <FormItem {...layout.inputLayout} label="Hide Empty Rows">
              {getFieldDecorator('hideEmptyRows', { valuePropName: 'checked' })(
                <Checkbox disabled={readonly.hideEmptyRows} style={{ paddingLeft: '5px' }} />
              )}
            </FormItem>
          )}
          {showAssignmentTitleFormat && (
            <FormItem {...layout.inputLayout} label="Title Format">
              {getFieldDecorator('assignmentTitleFormat')(
                <Select
                  style={{ paddingLeft: '5px' }}
                  mode="tags"
                  disabled={readonly.assignmentTitleFormat}
                >
                  {titleFormats.map(format => (
                    <Option key={format} value={format}>{format}</Option>
                  ))}
                </Select>
              )}
            </FormItem>
          )}
          {showTimeFilter && (
            <GenericErrorBoundary>
              <FormItem {...layout.inputLayout} label="Time Filter Start">
                {getFieldDecorator('timeFilterStart')(
                  <TimePicker placeholder="Start" disabled={readonly.timeFilter} allowClear minuteStep={15} defaultOpenValue={moment('00:00', timeFormat)} format={timeFormat} />
                )}
              </FormItem>
            </GenericErrorBoundary>
          )}
          {showTimeFilter && (
            <GenericErrorBoundary>
              <FormItem {...layout.inputLayout} label="Time Filter End">
                {getFieldDecorator('timeFilterEnd')(
                  <TimePicker placeholder="End" disabled={readonly.timeFilter} allowClear minuteStep={15} defaultOpenValue={moment('23:00', timeFormat)} format={timeFormat} />
                )}
              </FormItem>
            </GenericErrorBoundary>
          )}
        </fieldset>
      )}
      {(showDisplayTravelLayer || showDisplayLeaveLayer || showDisplayAvailabilityLayer) && (
        <fieldset>
          <legend>Timeline Layers</legend>
          {showDisplayTravelLayer && (
            <FormItem {...layout.inputLayout} label="Travel Bookings">
              {getFieldDecorator('displayTravelLayer', { valuePropName: 'checked' })(
                <Checkbox disabled={readonly.displayTravelLayer} style={{ paddingLeft: '5px' }} />
              )}
            </FormItem>
          )}
          {showDisplayLeaveLayer && (
            <FormItem {...layout.inputLayout} label="Leave">
              {getFieldDecorator('displayLeaveLayer', { valuePropName: 'checked' })(
                <Checkbox disabled={readonly.displayLeaveLayer} style={{ paddingLeft: '5px' }} />
              )}
            </FormItem>
          )}
          {showDisplayAvailabilityLayer && (
            <FormItem {...layout.inputLayout} label="Availability">
              {getFieldDecorator('displayAvailabilityLayer', { valuePropName: 'checked' })(
                <Checkbox disabled={readonly.displayAvailabilityLayer} style={{ paddingLeft: '5px' }} />
              )}
            </FormItem>
          )}
        </fieldset>
      )}
    </Form>
  );
};

FormInput.propTypes = {
  readonly: PropTypes.shape({
    activityCreateCount: PropTypes.bool,
    activityHoursBetween: PropTypes.bool,
    timelineMaxDays: PropTypes.bool,
    timelineSnapMinutes: PropTypes.bool,
    rosterPivot: PropTypes.bool,
    hideEmptyRows: PropTypes.bool,
    assignmentTitleFormat: PropTypes.bool,
    timeFilter: PropTypes.bool,
    displayTravelLayer: PropTypes.bool,
    displayLeaveLayer: PropTypes.bool,
    displayAvailabilityLayer: PropTypes.bool,
    hideUnallocatedRows: PropTypes.bool
  }),
  dataSource: PropTypes.object.isRequired,
  form: PropTypes.any.isRequired,
  optionFields: PropTypes.object,
  onFieldsChange: PropTypes.func,
  onValuesChange: PropTypes.func
};

FormInput.defaultProps = {
  readonly: {
    activityCreateCount: false,
    activityHoursBetween: false,
    timelineMaxDays: false,
    timelineSnapMinutes: false,
    rosterPivot: false,
    hideEmptyRows: false,
    assignmentTitleFormat: false,
    timeFilter: false,
    displayTravelLayer: false,
    displayLeaveLayer: false,
    displayAvailabilityLayer: false,
    hideUnallocatedRows: false
  },
  optionFields: {
    showActivityCreateCount: true,
    showActivityHoursBetween: true,
    showTimelineMaxDays: true,
    showTimelineSnapMinutes: true,
    showDisplayTravelLayer: false,
    showDisplayLeaveLayer: false,
    showDisplayAvailabilityLayer: false,
    showRosterPivot: true,
    showHideEmptyRows: true,
    showAssignmentTitleFormat: true,
    showTimeFilter: true,
    showHideUnallocatedRows: true
  },
  onFieldsChange() {
  },
  onValuesChange() {
  }
};

export default Form.create({
  mapPropsToFields,
  onFieldsChange,
  onValuesChange: _.debounce(onValuesChange, 300)
})(FormInput);
